/**
 * SETTINGS.WIDGET
 * Parameters for the account
 */

import { store } from "@/index"
import { withTranslation, WithTranslation } from "react-i18next"
import { useEffect, useState } from "react"
import { accountEdit, accountStatus, accountUpdate } from "@/redux/account.actions"
import { AccountOptions, AccountState } from "@/redux/account.types"
import {
  STATUS_LOADED,
  STATUS_LOADING,
  STATUS_LOAD_ERROR,
  STATUS_SAVED,
  STATUS_SAVE_ERROR,
  STATUS_SAVING
} from "@/redux/_status.types"
import { connect } from "react-redux"
import ListItem from "@/components/list-item"
import SaveIcon from "@/components/save-icon"
import Space from "@/components/space"
import { Session, SESSION_COLORS } from "@/redux/_session.types"
import { toast } from "react-toastify"
import PageLoader from "@/components/page-loader"
import DeleteAccountModal from "@/modals/delete-account.modal"
import PageHeader from "@/components/page-header"
import Button from "@/components/button"
import Tabs from "@/components/tabs"
import Card from "@/components/card"
import TextInput from "@/components/text-input"
import { sessionEdit, sessionEditAccountColors, sessionEditAccountOptions } from "@/redux/_session.actions"
import { subscriptionFetch, subscriptionGet } from "@/redux/subscription.actions"
import ModulesWidget from "./modules.widget"
import Link from "@/components/link"
import ColorPickerWidget from "./color-picker.widget"
import ImagePicker from "@/components/image-picker"
import Checkbox from "@/components/checkbox"
import ColorsPreviewWidget from "./colors-preview.widget"
import getBillings from "@/utils/get-billings.utils"
import BillingsWidget from "./billings.widget"
import LoginModal from "@/modals/login.modal"
import AdmSettingsSurveyWidget from "./adm-settings-survey.widget"
import AdmSettingsRisksWidget from "./adm-settings-risks.widget"
import AdmFiltersWidget from "./adm-filters.widget"
import establishmentStore from "@/features/adm/entities/establishment/model/establishment.store"
import { useAppDispatch } from "@/core/root-reducer"
import { getImageUrl } from "@/utils/get-image-url"
import ListButton from "@/components/list-button"
import { faCopy } from "@fortawesome/free-solid-svg-icons"
import FilterSelect from "@/components/filter-select"
import ModalConfirm from "@/components/modal-confirm"
import { useNavigate } from "react-router-dom"
import { Role } from "@/redux/role.types"
import { roleFetch, roleSave, roleUpdateRules } from "@/redux/role.actions"

interface StateProps {
  _session: Session
  account: AccountState
}

type Props = StateProps & WithTranslation

//Modals
const MODAL_DELETE: string = "MODAL_DELETE"
const MODAL_LOGIN_SUPERADMIN: string = "MODAL_LOGIN_SUPERADMIN"

//List of tabs
const TAB_GENERAL: string = "TAB_GENERAL"
const TAB_SECURITY: string = "TAB_SECURITY"
const TAB_SKIN: string = "TAB_SKIN"
const TAB_FILTERS: string = "TAB_FILTERS"
const TAB_MODULE_SURVEY: string = "TAB_MODULE_SURVEY"
const TAB_MODULE_RISKS: string = "TAB_MODULE_RISKS"

function AdmSettingsWidget(props: Props) {
  const { t } = props
  const rolesEnabled = props._session.accountOptions.roleManagementEnabled
  const managerFilters = props._session.accountOptions.managerFilters ?? []
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  //After the manager filters are saved, propose the role "Managers" if the account has none
  const [isManagerRoleProposed, setIsManagerRoleProposed] = useState(false)
  const [isManagerRoleSaving, setIsManagerRoleSaving] = useState(false)
  //Checking the box shows the manager filters with their modal open
  const [isManagerFiltersShown, setIsManagerFiltersShown] = useState(false)

  //Modal that will be displayed
  const [currentModal, setCurrentModal] = useState<string | null>(null)

  //List of tabs
  const [tabs] = useState(initTabs())

  //Current active tab
  const [currentTab, setCurrentTab] = useState(initCurrentTab())

  //Load subscription when page is loaded
  useEffect(() => {
    loadPage()

    //Load all the data of the page
    //Packs + subscriptions + remaining invitations
    async function loadPage() {
      store.dispatch(accountStatus(STATUS_LOADING))
      const subscriptions: any = await store.dispatch(subscriptionFetch())

      if (subscriptions.error) {
        store.dispatch(accountStatus(STATUS_LOAD_ERROR))
      } else {
        store.dispatch(accountStatus(STATUS_LOADED))
        store.dispatch(subscriptionGet(subscriptions))
      }
    }
  }, [])

  useEffect(() => {
    if (!props._session.features.risks) return

    async function loadEstablishment() {
      try {
        const establishments = await dispatch(establishmentStore.thunks.fetchAndSet()).unwrap()
        const establishment = establishments[0]
        dispatch(establishmentStore.actions.setList([establishment]))
        dispatch(establishmentStore.actions.setActive(establishment))
      } catch (e) {
        console.error("Impossible de charger l'établissement :", e)
      }
    }
    loadEstablishment()
  }, [])

  function editPrimaryColor(color: string) {
    saveColor("brandPrimary", color)
    if (props._session.interfaceType === "ADMIN_PARENT") {
      store.dispatch(sessionEdit("superaccountColor", color))
    }
  }

  //Init list of tabls
  function initTabs() {
    const tabs: Record<string, string> = {
      TAB_GENERAL: t("account_settings_general"),
      TAB_SECURITY: t("account_settings_security"),
      TAB_SKIN: t("account_settings_skin")
    }

    if (props._session.features.survey) {
      tabs.TAB_MODULE_SURVEY = t("surveys")
      tabs.TAB_FILTERS = t("filters_custom")
    }

    if (props._session.features.risks) {
      tabs.TAB_MODULE_RISKS = "DUERP"
    }

    return tabs
  }

  //Get initial tab (if reload from stay on tab)
  function initCurrentTab() {
    const searchParams = new URLSearchParams(window.location.search)
    const tab = searchParams.get("tab")
    window.history.pushState({}, "", window.location.pathname)
    return tab ? tab : TAB_GENERAL
  }

  //Edit account name
  function handleChanges(event) {
    store.dispatch(sessionEdit(event.id, event.value))
  }

  //Set security
  function handleIsPasswordRequired() {
    const isPasswordRequired = !props._session.isPasswordRequired
    saveAccount(props._session.accountOptions, isPasswordRequired)
    store.dispatch(sessionEdit("isPasswordRequired", isPasswordRequired))
  }

  //Save data to database
  //SSO login link of the account
  function getSsoLink() {
    return window.location.origin + "/login/sso/auth/" + props._session.accountOptions.ssoOrganization
  }

  function copySsoLink() {
    navigator.clipboard.writeText(getSsoLink())
    toast(t("utils_copy_link_done"))
  }

  async function saveAccount(options, isPasswordRequired?) {
    const account = {
      id: props._session.accountId,
      name: props._session.accountName,
      options,
      colors: props._session.accountColors,
      isPasswordRequired: isPasswordRequired !== undefined ? isPasswordRequired : props._session.isPasswordRequired
    }

    store.dispatch(accountStatus(STATUS_SAVING))
    const response: any = await store.dispatch(accountUpdate(account))
    store.dispatch(accountStatus(response.error ? STATUS_SAVE_ERROR : STATUS_SAVED))

    if (!response.error) {
      toast(t("utils_saved"), { autoClose: 2000 })
    }
    return !response.error
  }

  //Les options de périmètre du tableau de bord (et les filtres manager) partagent le même enregistrement
  async function saveDashboardScopeOption(key: keyof AccountOptions, value: boolean | string[]) {
    const options = { ...props._session.accountOptions, [key]: value }
    const saved = await saveAccount(options)
    if (saved) {
      store.dispatch(sessionEditAccountOptions(key, value))
    }
    return saved
  }

  async function saveManagerFilters(names: string[]) {
    const saved = await saveDashboardScopeOption("managerFilters", names)
    if (saved && names.length) {
      await dispatch(roleFetch())
      const roles = store.getState().roles
      setIsManagerRoleProposed(roles.status === "loaded" && !roles.list.some((role) => role.managerScope))
    }
    return saved
  }

  //A role is created by name first, then turned into a role "Managers"
  async function createManagerRole() {
    setIsManagerRoleSaving(true)
    const id = await dispatch(roleSave(new Role({ AccountId: props._session.accountId, name: t("roles_members_managers") }), true))
    const role = store.getState().roles.list.find((item) => item.id === id)
    const isSaved = role && (await dispatch(roleUpdateRules(role, { managerScope: true })))
    setIsManagerRoleSaving(false)
    setIsManagerRoleProposed(false)
    if (isSaved) navigate(`/adm/restricted-access/${id}`)
  }

  //Save color
  function saveColor(id, color) {
    store.dispatch(sessionEditAccountColors(id, color))
    store.dispatch(accountEdit("colors", { ...props._session.accountColors, [id]: color }))
    setTimeout(() => {
      saveAccount(props._session.accountOptions)
    }, 500)
  }

  //Save options
  //Timeout of 1000ms before save
  function saveImage(value) {
    const options = Object.assign({}, props._session.accountOptions, {
      image: value
    })
    store.dispatch(sessionEdit("accountOptions", options))

    setTimeout(() => {
      saveAccount(options)

      if (props._session.interfaceType === "ADMIN_PARENT") {
        store.dispatch(sessionEdit("superaccountImage", value))
      }
    }, 500)
  }

  //Disable the chat widget
  function toogleChat() {
    store.dispatch(sessionEditAccountOptions("disableChat", !props._session.accountOptions.disableChat))
    setTimeout(() => {
      saveAccount(props._session.accountOptions)
      window.location.href = window.location.href + "?tab=TAB_SKIN"
    }, 500)
  }

  return (
    <div>
      {currentModal === MODAL_DELETE && (
        <DeleteAccountModal
          accountId={props._session.accountId}
          isAsSuperadmin={props._session.connectedAsSuperadmin && props._session.accountIsChildren}
          onClose={() => setCurrentModal(null)}
          onDelete={props._session.connectedAsSuperadmin ? () => setCurrentModal(MODAL_LOGIN_SUPERADMIN) : undefined}
        />
      )}

      {isManagerRoleProposed && (
        <ModalConfirm
          status={isManagerRoleSaving ? STATUS_SAVING : STATUS_LOADED}
          onNo={() => setIsManagerRoleProposed(false)}
          onYes={createManagerRole}
          text={t("account_manager_role_create_help")}
          textBold={t("account_manager_role_create")}
        />
      )}

      {currentModal === MODAL_LOGIN_SUPERADMIN && (
        <LoginModal
          authLevel={2}
          onClose={() => setCurrentModal(null)}
          email={props._session.email}
          accountIdForSuperadmin={props._session.connectedAsSupervisor ? props._session.superaccountId : undefined}
          isSupervisor={props._session.connectedAsSupervisor}
        />
      )}

      <PageHeader title={props._session.interfaceType === "ADMIN_PARENT" ? t("utils_params") : t("account_settings")}>
        <Button onClick={() => setCurrentModal(MODAL_DELETE)}>
          {props._session.interfaceType === "ADMIN_PARENT" ? t("account_delete_superadmin") : t("account_delete")}
        </Button>
      </PageHeader>

      <Tabs active={currentTab} onClick={(tab) => setCurrentTab(tab)} tabs={tabs} />

      <PageLoader status={props.account.status}>
        {currentTab === TAB_GENERAL && (
          <>
            <Card>
              <TextInput
                id="accountName"
                onBlur={() => saveAccount(props._session.accountOptions)}
                onChange={handleChanges}
                status={props.account.status}
                title={t("account_create_name")}
                value={props._session.accountName}
              />

              <div className="height-20" />

              <div className="flex">
                <div className="flex1">
                  <h3>{t("account_modules")}</h3>
                  <ModulesWidget features={props._session.features} modules={props._session.modules} readonly />
                  <div style={{ height: "8px" }} />
                  <Link isWithoutMargin onClick={() => window.open("https://www.id-tree.com/lp-contact", "_blank")}>
                    {t("utils_contact")}
                  </Link>
                </div>

                <div className="width-20" />

                <div className="flex1">
                  <h3>{t("account_invitations")}</h3>
                  <p>{props._session.accountInvitations}</p>
                  <Link isWithoutMargin onClick={() => window.open("https://www.id-tree.com/lp-contact", "_blank")}>
                    {t("utils_contact")}
                  </Link>
                </div>
              </div>

              <div className="height-20" />

              {getBillings().length > 0 && (
                <BillingsWidget billings={getBillings()} showPaymentStatus title={t("account_billing")} />
              )}
            </Card>
          </>
        )}

        {currentTab === TAB_SECURITY && (
          <div>
            <p className="grey-t">{t("admin_security_help")}</p>

            <div className="height-20" />

            <Card isWithoutPadding>
              {props._session.accountOptions.sso ? (
                <ListItem>
                  <div className="flex1">
                    <p>
                      <b>{t("admin_sso_activated")}</b>
                    </p>
                    <div className="text-input-title flex">
                      <span className="grey-t">{t("account_sso_link")}</span>
                    </div>
                    <div className="flex items-center my-10">
                      <span>{getSsoLink()}</span>
                      <ListButton icon={faCopy} text={t("utils_copy_link")} onClick={copySsoLink} />
                    </div>
                  </div>
                </ListItem>
              ) : (
                <ListItem>
                  <div>
                    <p>
                      <b>{t("admin_security_admin_level")}</b>
                    </p>
                    <p className="grey-t">{t("admin_security_admin_level_help")}</p>

                    <div className="height-10" />

                    <Checkbox
                      active={props._session.isPasswordRequired}
                      text={t("admin_security_level")}
                      onClick={handleIsPasswordRequired}
                      status={props.account.status}
                    />

                    <div className="height-10" />
                  </div>
                </ListItem>
              )}
            </Card>
          </div>
        )}

        {currentTab === TAB_SKIN && (
          <Card>
            <div className="flex">
              <div>
                <h3>{t("account_logo")}</h3>

                <ImagePicker
                  id={props._session.accountId}
                  square
                  backgroundColor={props._session.accountColors.brandPrimary}
                  imageId={props._session.accountOptions.image ? props._session.accountId : null}
                  model="Accounts"
                  onDelete={() => saveImage(false)}
                  onSave={() => saveImage(true)}
                />
              </div>

              <Space />

              <div className="width-100" style={{ margin: 0 }}>
                <ColorsPreviewWidget account={props.account.active} />
              </div>
            </div>

            <div className="height-20" />

            <div className="flex">
              <h3>{t("utils_colors")}</h3>
              <Space />
              <SaveIcon status={props.account.status} />
            </div>

            <div className="flex">
              <div className="flex1">
                <ColorPickerWidget
                  colors={SESSION_COLORS}
                  isCustom
                  title={t("account_color_primary")}
                  selectedColor={props._session.accountColors.brandPrimary}
                  onSelect={(color: string) => editPrimaryColor(color)}
                  imageUrl={
                    props._session.accountOptions.image ? getImageUrl("Accounts", props._session.accountId) : undefined
                  }
                />

                <ColorPickerWidget
                  colors={SESSION_COLORS}
                  isCustom
                  title={t("account_color_secondary")}
                  selectedColor={props._session.accountColors.brandSecondary}
                  onSelect={(color: string) => saveColor("brandSecondary", color)}
                  imageUrl={
                    props._session.accountOptions.image ? getImageUrl("Accounts", props._session.accountId) : undefined
                  }
                />

                <ColorPickerWidget
                  colors={SESSION_COLORS}
                  isCustom
                  title={t("account_color_text")}
                  selectedColor={props._session.accountColors.brandText}
                  onSelect={(color: string) => saveColor("brandText", color)}
                  imageUrl={
                    props._session.accountOptions.image ? getImageUrl("Accounts", props._session.accountId) : undefined
                  }
                />
              </div>

              <div className="flex1">
                <ColorPickerWidget
                  colors={SESSION_COLORS}
                  isCustom
                  title={t("account_color_cta")}
                  selectedColor={props._session.accountColors.cta}
                  onSelect={(color: string) => saveColor("cta", color)}
                  imageUrl={
                    props._session.accountOptions.image ? getImageUrl("Accounts", props._session.accountId) : undefined
                  }
                />

                <ColorPickerWidget
                  colors={SESSION_COLORS}
                  isCustom
                  title={t("account_color_active")}
                  selectedColor={props._session.accountColors.active}
                  onSelect={(color: string) => saveColor("active", color)}
                  imageUrl={
                    props._session.accountOptions.image ? getImageUrl("Accounts", props._session.accountId) : undefined
                  }
                />

                <ColorPickerWidget
                  colors={SESSION_COLORS}
                  isCustom
                  title={t("account_color_email")}
                  selectedColor={props._session.accountColors.email}
                  onSelect={(color: string) => saveColor("email", color)}
                  imageUrl={
                    props._session.accountOptions.image ? getImageUrl("Accounts", props._session.accountId) : undefined
                  }
                />
              </div>
            </div>

            {
              /** chat option only if language is french */
              props._session.language === "fr" && (
                <div className="flex" style={{ marginTop: 32 }}>
                  <Checkbox
                    active={!props._session.accountOptions.disableChat}
                    text={t("account_settings_chat")}
                    onClick={toogleChat}
                  />
                  <Space />
                </div>
              )
            }
          </Card>
        )}

        {currentTab === TAB_FILTERS && (
          <AdmFiltersWidget>
            <Checkbox
              id="roleManagementEnabled"
              active={props._session.accountOptions.roleManagementEnabled ?? false}
              text={t("account_role_management_enabled")}
              onClick={(event: { value: boolean }) => saveDashboardScopeOption("roleManagementEnabled", event.value)}
              disabled={props.account.status === STATUS_SAVING}
              status={props.account.status}
            />

            {/** Filtres manager, du niveau le plus haut au manager direct, pour le rôle « Managers ».
                Cocher ouvre leur choix, décocher les retire. */}
            <Checkbox
              id="managerFilters"
              active={rolesEnabled && managerFilters.length > 0}
              text={t("account_manager_filters")}
              onClick={(event: { value: boolean }) =>
                event.value ? setIsManagerFiltersShown(true) : saveDashboardScopeOption("managerFilters", [])
              }
              disabled={!rolesEnabled || props.account.status === STATUS_SAVING}
              status={props.account.status}
            />
            {rolesEnabled && (managerFilters.length > 0 || isManagerFiltersShown) && (
              <div style={{ margin: "0 0 10px 38px" }}>
                <FilterSelect
                  names={managerFilters}
                  title={t("account_manager_filters")}
                  help={t("account_manager_filters_select")}
                  example={t("account_manager_filters_example")}
                  isOrdered
                  isOpenAtStart={isManagerFiltersShown}
                  onSave={saveManagerFilters}
                  onClose={() => setIsManagerFiltersShown(false)}
                />
              </div>
            )}

            {/** Ces deux options gardent leur valeur en base mais restent
                désactivées tant que la gestion par rôles décide du périmètre. */}
            <Checkbox
              id="observersSeeAllResults"
              active={props._session.accountOptions.observersSeeAllResults}
              text={t("manager_see_all_results")}
              onClick={(event: { value: boolean }) => saveDashboardScopeOption("observersSeeAllResults", event.value)}
              disabled={rolesEnabled || props.account.status === STATUS_SAVING}
              status={props.account.status}
            />

            <Checkbox
              id="isObserverNotRestricted"
              active={props._session.accountOptions.isObserverNotRestricted}
              text={t("admin_observer_restricted")}
              onClick={(event: { value: boolean }) => saveDashboardScopeOption("isObserverNotRestricted", event.value)}
              disabled={rolesEnabled || props.account.status === STATUS_SAVING}
              status={props.account.status}
            />
          </AdmFiltersWidget>
        )}
        {currentTab === TAB_MODULE_SURVEY && <AdmSettingsSurveyWidget />}
        {currentTab === TAB_MODULE_RISKS && <AdmSettingsRisksWidget />}
      </PageLoader>
    </div>
  )
}

const mapStateToProps = (state) => ({
  _session: state._session,
  account: state.account
})

export default connect(mapStateToProps)(withTranslation()(AdmSettingsWidget))
